/**
 * Minimal OKLCH colour math so derived tokens match the design's CSS
 * `color-mix(in oklch, <a> p%, <b>)` exactly enough, but as concrete hex values
 * that MUI can manipulate (alpha, contrast text) and that PDFs can use.
 */
type Rgb = [number, number, number];
type Oklch = { l: number; c: number; h: number | null };

export function hexToRgb(hex: string): Rgb {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) throw new Error(`Invalid hex colour: ${hex}`);
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex([r, g, b]: Rgb): string {
  const to = (v: number) =>
    Math.round(Math.min(255, Math.max(0, v)))
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
}

const toLinear = (v: number) => {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const fromLinear = (c: number) => 255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function rgbToOklch(rgb: Rgb): Oklch {
  const [r, g, b] = rgb.map(toLinear) as Rgb;
  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  const c = Math.hypot(A, B);
  // CSS: hue is "powerless" for achromatic colours and takes the other colour's hue when mixing.
  // 0.02 reproduces Chromium's output for the design's near-black #121318 (checked in color.test.ts).
  const h = c < 0.02 ? null : ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
}

function oklchToRgb({ l, c, h }: Oklch): Rgb {
  const hr = ((h ?? 0) * Math.PI) / 180;
  const A = c * Math.cos(hr);
  const B = c * Math.sin(hr);
  const l_ = (l + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m_ = (l - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s_ = (l - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    fromLinear(4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_),
    fromLinear(-1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_),
    fromLinear(-0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_),
  ];
}

/** Equivalent of CSS `color-mix(in oklch, a <weightA>%, b)`, hue interpolated the shorter way. */
export function mixOklch(a: string, weightA: number, b: string): string {
  const p = weightA / 100;
  const ca = rgbToOklch(hexToRgb(a));
  const cb = rgbToOklch(hexToRgb(b));
  let h: number | null;
  if (ca.h === null) h = cb.h;
  else if (cb.h === null) h = ca.h;
  else {
    let d = cb.h - ca.h;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    h = (ca.h + d * (1 - p) + 360) % 360;
  }
  return rgbToHex(oklchToRgb({ l: ca.l * p + cb.l * (1 - p), c: ca.c * p + cb.c * (1 - p), h }));
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(fg: string, bg: string): number {
  const a = relativeLuminance(fg);
  const b = relativeLuminance(bg);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** rgba() string from a hex colour, for state layers and disabled colours. */
export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
